import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import ChildChatView from '@/views/ChildChatView.vue'
import EnglishLearningView from '@/views/EnglishLearningView.vue'

// 登录相关页面
const LoginView = () => import('@/views/login/LoginView.vue');
const WechatConfirmView = () => import('@/views/login/WechatConfirmView.vue');
const DingtalkCallbackView = () => import('@/views/login/DingtalkCallback.vue');
const MiniAppRedirectView = () => import('@/views/MiniAppRedirect.vue');
const MiniAppTestView = () => import('@/views/MiniAppTest.vue');

// 题目相关页面
const ProblemListView = () => import('@/views/problem/ProblemListView.vue');

// 考试相关页面
const ExamListView = () => import('@/views/exam/ExamListView.vue');
const ExamCreateView = () => import('@/views/exam/ExamCreateView.vue');
const ExamGenerateView = () => import('@/views/exam/ExamGenerateView.vue');

// 答题记录相关页面
const RecordListView = () => import('@/views/record/RecordListView.vue');

// 字典管理相关页面
const DictManagerView = () => import('@/views/dictionary/DictionaryView.vue');

// 竞赛结果页面
const CompetitionResultView = () => import('@/views/CompetitionResultView.vue');

// 问数功能页面
const ChatDataView = () => import('@/views/ChatDataView.vue');

// 股票行情页面
const StockQuoteView = () => import('@/views/stock/StockQuoteView.vue');
const WatchListView = () => import('@/views/stock/WatchListView.vue');
const StockAlertView = () => import('@/views/stock/StockAlertView.vue');
const StrategyBacktestView = () => import('@/views/stock/StrategyBacktestView.vue');
const VirtualTradeView = () => import('@/views/stock/VirtualTradeView.vue');
const SmartTradeView = () => import('@/views/stock/SmartTradeView.vue');
const SentimentAnalysisView = () => import('@/views/stock/SentimentAnalysisView.vue');

// 知识点列表页面
const KnowledgePointListView = () => import('@/views/KnowledgePointListView.vue');

// 徒步记录页面
const HikingListView = () => import('@/views/HikingListView.vue');

// 听写功能页面
const DictationView = () => import('@/views/DictationView.vue');
const DictationRecordView = () => import('@/views/DictationRecordView.vue');
const DictationWrongSetView = () => import('@/views/DictationWrongSetView.vue');

// 语音设置页面
const SpeechSettingsView = () => import('@/views/SpeechSettingsView.vue');

// 测试描红纸页面
const TestTracePaper = () => import('@/views/TestTracePaper.vue');

// OSS文件管理页面
const OssFileListView = () => import('@/views/OssFileListView.vue');


// 错误页面
const NotFound = () => import('@/views/error/ErrorView.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/child-chat'
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView
  },
  {
    path: '/wechat/confirm',
    name: 'WechatConfirm',
    component: WechatConfirmView
  },
  {
    path: '/dingtalk/callback',
    name: 'DingtalkCallback',
    component: DingtalkCallbackView
  },
  {
    path: '/miniapp',
    name: 'MiniAppRedirect',
    component: MiniAppRedirectView
  },
  {
    path: '/miniapp/test',
    name: 'MiniAppTest',
    component: MiniAppTestView
  },
  {
    path: '/hiking',
    name: 'HikingList',
    component: HikingListView
  },
  {
    path: '/problem',
    children: [
      {
        path: 'list',
        name: 'ProblemList',
        component: ProblemListView
      }
    ]
  },
  {
    path: '/exam',
    children: [
      {
        path: 'list',
        name: 'ExamList',
        component: ExamListView
      },
      {
        path: 'create',
        name: 'ExamCreate',
        component: ExamCreateView
      },
      {
        path: 'generate/:id',
        name: 'ExamGenerate',
        component: ExamGenerateView,
        props: true
      }
    ]
  },
  {
    path: '/record',
    children: [
      {
        path: 'list',
        name: 'RecordList',
        component: RecordListView
      }
    ]
  },
  {
    path: '/dict',
    children: [
      {
        path: 'manager',
        name: 'DictManager',
        component: DictManagerView
      }
    ]
  },
  {
    path: '/competition',
    children: [
      {
        path: 'results',
        name: 'CompetitionResults',
        component: CompetitionResultView
      },
      {
        path: 'chat-data',
        name: 'ChatData',
        component: ChatDataView,
        meta: { requiresAuth: true, title: '智能问数' }
      }
    ]
  },
  {
    path: '/stock',
    children: [
      {
        path: 'quote/:code?',
        name: 'StockQuote',
        component: StockQuoteView,
        meta: { requiresAuth: true, title: '股票行情' }
      },
      {
        path: 'watchlist',
        name: 'WatchList',
        component: WatchListView,
        meta: { requiresAuth: true, title: '自选股' }
      },
      {
        path: 'alert',
        name: 'StockAlert',
        component: StockAlertView,
        meta: { requiresAuth: true, title: '预警管理' }
      },
      {
        path: 'backtest',
        name: 'StrategyBacktest',
        component: StrategyBacktestView,
        meta: { requiresAuth: true, title: '策略回测' }
      },
      {
        path: 'virtual-trade',
        name: 'VirtualTrade',
        component: VirtualTradeView,
        meta: { requiresAuth: true, title: '模拟交易' }
      },
      {
        path: 'smart-trade',
        name: 'SmartTrade',
        component: SmartTradeView,
        meta: { requiresAuth: true, title: '智能选股交易' }
      },
      {
        path: 'sentiment-analysis',
        name: 'SentimentAnalysis',
        component: SentimentAnalysisView,
        meta: { requiresAuth: true, title: '舆情分析可视化' }
      }
    ]
  },
  {
    path: '/knowledge-points',
    name: 'KnowledgePointList',
    component: KnowledgePointListView,
    meta: { requiresAuth: true, title: '知识点管理' }
  },
  {
    path: '/hiking',
    name: 'HikingList',
    component: HikingListView
  },
  {
    path: '/dictation',
    children: [
      {
        path: 'start',
        name: 'DictationStart',
        component: DictationView
      },
      {
        path: 'records',
        name: 'DictationRecords',
        component: DictationRecordView
      },
      {
        path: 'wrong-sets',
        name: 'DictationWrongSets',
        component: DictationWrongSetView
      }
    ]
  },
  {
    path: '/settings/speech',
    name: 'SpeechSettings',
    component: SpeechSettingsView
  },
  {
    path: '/test/trace-paper',
    name: 'TestTracePaper',
    component: TestTracePaper
  },
  {
    path: '/oss-files',
    name: 'OssFileList',
    component: OssFileListView,
    meta: { requiresAuth: true, title: 'OSS文件管理' }
  },
  {
    path: '/child-chat',
    name: 'ChildChat',
    component: ChildChatView,
    meta: { requiresAuth: true }
  },
  {
    path: '/ear-training',
    name: 'EarTrainingView',
    component: () => import('@/views/EarTrainingView.vue'),
    meta: { requiresAuth: true, title: '英语磨耳朵' }
  },
  {
    path: '/english-learning',
    name: 'EnglishLearning',
    component: EnglishLearningView,
    meta: { title: '英语学习' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// 全局前置守卫
router.beforeEach((to, _, next) => {
  const userStore = useUserStore();
  
  // 如果用户未登录且目标页面不是登录相关页面，则重定向到登录页
  if (!userStore.isAuthenticated && to.name !== 'Login' && to.name !== 'WechatConfirm') {
    next({ name: 'Login' });
  } else {
    next();
  }
});

export default router;